import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

function recordPath(n) {
  if (n.task_id) return `/tasks/${n.task_id}`;
  if (n.lead_id) return `/leads/${n.lead_id}`;
  if (n.asset_id) return `/assets/${n.asset_id}`;
  if (n.broker_id) return `/brokers/${n.broker_id}`;
  if (n.match_id) return "/kanban";
  return null;
}

const TONE = {
  critical: "border-l-red-500",
  warning: "border-l-amber-500",
  info: "border-l-slate-300",
};

export default function NotificationBell({ variant = "light" }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: countData } = useQuery({
    queryKey: ["notif-unread-count"],
    queryFn: async () => (await api.get("/notifications/unread-count")).data,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });
  const unread = countData?.count || 0;

  const { data: items = [] } = useQuery({
    queryKey: ["notif-list"],
    queryFn: async () => (await api.get("/notifications", { params: { include_acknowledged: true, limit: 10 } })).data,
    enabled: open,
    refetchInterval: open ? 60000 : false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["notif-unread-count"] });
    qc.invalidateQueries({ queryKey: ["notif-list"] });
  };

  const ackOne = useMutation({
    mutationFn: async (id) => api.post(`/notifications/${id}/acknowledge`),
    onSuccess: refresh,
  });
  const readAll = useMutation({
    mutationFn: async () => api.post("/notifications/read-all"),
    onSuccess: refresh,
  });

  const onOpen = (n) => {
    if (!n.acknowledged) ackOne.mutate(n.id);
    const path = recordPath(n);
    setOpen(false);
    if (path) navigate(path);
  };

  const iconColor = variant === "dark" ? "text-slate-200 hover:text-white" : "text-slate-600 hover:text-slate-900";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative flex items-center justify-center h-10 w-10 rounded-md hover:bg-white/10 ${iconColor}`}
          aria-label="Notifications"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-unread-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="font-display text-sm font-semibold text-slate-900">Notifications</div>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => readAll.mutate()}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900"
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-50">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="notification-empty">
              You're all caught up.
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => onOpen(n)}
                className={`w-full text-left px-4 py-3 border-l-2 ${TONE[n.severity] || TONE.info} ${n.acknowledged ? "bg-white" : "bg-amber-50/40"} hover:bg-slate-50`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.acknowledged && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{n.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</div>
                    <div className="text-[10px] text-slate-400 mt-1 font-mono-num">{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
        <button
          type="button"
          onClick={() => { setOpen(false); navigate("/notifications"); }}
          className="w-full text-center py-2.5 text-xs font-semibold text-slate-500 hover:text-slate-900 border-t border-slate-100"
          data-testid="notification-view-all"
        >
          View all
        </button>
      </PopoverContent>
    </Popover>
  );
}
